// ============================================================================
// 타이틀 BGM — **실시간 합성 시퀀서.** 오디오 파일이 없다 (절대 규칙: 클립 재생 금지).
//
// 왜 시퀀서인가:
//   · 파일이 없으니 이음새(seam)가 구조적으로 없다. 루프 지점에 클릭 노이즈가 못 생긴다.
//   · 8마디를 다 돌면 step 이 0 으로 돌아갈 뿐이라 무한 루프가 공짜다.
//   · 전송량 0KB. 번들 크기에 영향이 없다.
//
// 구조 — 5성부, 전부 AudioEngine 의 master 아래로 들어간다 (음소거·마스터 볼륨 공유):
//   lead  square→lowpass  16분 멜로디. 밝은 주선율
//   chop  square→lowpass  뒷박(오프비트) 화음 스탭. "발랄함"의 대부분이 여기서 온다
//   bass  triangle        루트/5도 8분. 바닥을 잡는다
//   bell  sine×2배음      마디 첫박의 반짝임
//   drum  noise/sine      킥(사인 하강) · 스네어(노이즈 밴드) · 하이햇(노이즈 하이패스)
//
// 스케줄링: 매 프레임 tick(dt) 이 lookahead 초 앞까지의 스텝을 미리 예약한다.
// requestAnimationFrame 은 흔들리지만 예약 시각은 ctx.currentTime 기준이라 박자가 안 흔들린다.
// 탭이 백그라운드로 가서 프레임이 멈춰도, 돌아오면 밀린 스텝을 건너뛰고 현재 시각에 다시 붙는다.
// ============================================================================
import { P } from './params';

const STEPS_PER_BAR = 16; // 16분음표 격자
const BARS = 8;
const TOTAL_STEPS = STEPS_PER_BAR * BARS;

/** MIDI 노트 번호 → Hz */
function hz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

// ---------------------------------------------------------------- 곡
// C 장조. I–V–vi–IV | I–V–IV–V. 마디마다 코드 하나.
const ROOTS = [48, 43, 45, 41, 48, 43, 41, 43]; // C3 G2 A2 F2 | C3 G2 F2 G2
const TRIADS: number[][] = [
  [60, 64, 67], // C
  [59, 62, 67], // G
  [60, 64, 69], // Am
  [60, 65, 69], // F
  [60, 64, 67], // C
  [59, 62, 67], // G
  [60, 65, 69], // F
  [59, 62, 67], // G
];

/** [스텝, MIDI, 길이(스텝)] — 마디별 멜로디 */
const MELODY: [number, number, number][][] = [
  // A 악절 — 올라갔다 내려온다
  [[0, 72, 2], [2, 76, 2], [4, 79, 2], [6, 76, 2], [8, 74, 4], [12, 72, 4]],
  [[0, 71, 2], [2, 74, 2], [4, 79, 2], [6, 78, 1], [7, 79, 1], [8, 81, 4], [12, 79, 4]],
  [[0, 69, 2], [2, 72, 2], [4, 76, 2], [6, 72, 2], [8, 74, 4], [12, 71, 4]],
  [[0, 77, 2], [2, 76, 2], [4, 74, 2], [6, 72, 2], [8, 74, 8]],
  // B 악절 — 한 옥타브 위로 치고 올라간다 (같은 8마디를 두 번 듣게 되므로 대비를 준다)
  [[0, 79, 2], [2, 76, 2], [4, 72, 2], [6, 76, 2], [8, 79, 2], [10, 81, 2], [12, 83, 4]],
  [[0, 84, 4], [4, 83, 2], [6, 81, 2], [8, 79, 4], [12, 74, 4]],
  [[0, 77, 2], [2, 81, 2], [4, 84, 4], [8, 81, 2], [10, 77, 2], [12, 76, 4]],
  [[0, 74, 2], [2, 71, 2], [4, 74, 2], [6, 79, 2], [8, 78, 4], [12, 79, 4]], // 마지막 F#→G 로 C 로 돌아갈 길을 연다
];

// 베이스: 8분. 루트 루트 5도 루트 …
const BASS_STEPS = [0, 2, 4, 6, 8, 10, 12, 14];
const BASS_INT = [0, 0, 7, 0, 12, 0, 7, 0]; // 루트에서의 반음 간격
// 킥 / 스네어 / 하이햇
const KICK = [0, 6, 8];
const SNARE = [4, 12];
const HAT = [0, 2, 4, 6, 8, 10, 12, 14];

export class Bgm {
  private ctx: AudioContext | null = null;
  private bus: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private step = 0; // 다음에 예약할 스텝 (0..TOTAL_STEPS-1)
  private nextTime = 0; // 그 스텝의 예약 시각 (ctx 시간)
  private fadeOutAt = -1; // 페이드아웃 종료 시각. 음수면 페이드 중이 아니다
  playing = false;
  /** 진단용 — 지금 몇 번째 스텝을 예약했나 */
  get debugStep(): number {
    return this.step;
  }

  /** AudioEngine 이 컨텍스트를 만든 뒤에 부른다. 여러 번 불러도 안전. */
  attach(ctx: AudioContext, master: GainNode): void {
    if (this.ctx === ctx) return;
    this.ctx = ctx;
    this.bus = ctx.createGain();
    this.bus.gain.value = 0;
    this.bus.connect(master);
    // 재질음과 같은 결정적 노이즈 (audio.ts 와 같은 xorshift). 하이햇·스네어용.
    const n = Math.floor(ctx.sampleRate * 1);
    const buf = ctx.createBuffer(1, n, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let s = 0x2545f491;
    for (let i = 0; i < n; i++) {
      s ^= s << 13;
      s ^= s >>> 17;
      s ^= s << 5;
      d[i] = ((s >>> 0) / 4294967296) * 2 - 1;
    }
    this.noise = buf;
  }

  /** 곡을 처음부터 시작한다. 이미 돌고 있으면 페이드아웃만 취소한다. */
  start(): void {
    if (!this.ctx || !this.bus) return;
    const t = this.ctx.currentTime;
    this.bus.gain.cancelScheduledValues(t);
    this.bus.gain.setValueAtTime(this.bus.gain.value, t);
    this.bus.gain.linearRampToValueAtTime(P.bgm.gain, t + P.bgm.fadeIn);
    this.fadeOutAt = -1;
    if (this.playing) return;
    this.playing = true;
    this.step = 0;
    this.nextTime = t + 0.06; // 첫 음이 잘리지 않게 아주 살짝 뒤에서 시작
  }

  /** fade 초에 걸쳐 끄고, 다 끄면 예약을 멈춘다. */
  stop(fade = P.bgm.fadeOut): void {
    if (!this.ctx || !this.bus || !this.playing) return;
    const t = this.ctx.currentTime;
    const f = Math.max(0.01, fade);
    this.bus.gain.cancelScheduledValues(t);
    this.bus.gain.setValueAtTime(this.bus.gain.value, t);
    this.bus.gain.linearRampToValueAtTime(0, t + f);
    this.fadeOutAt = t + f;
  }

  /** 즉시 정지 (리셋·화면 전환 등) */
  kill(): void {
    if (!this.bus || !this.ctx) return;
    this.bus.gain.cancelScheduledValues(this.ctx.currentTime);
    this.bus.gain.value = 0;
    this.playing = false;
    this.fadeOutAt = -1;
  }

  /** 매 프레임. lookahead 앞까지의 스텝을 예약한다. */
  tick(): void {
    if (!this.playing || !this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;
    if (this.fadeOutAt >= 0 && now >= this.fadeOutAt) {
      this.playing = false;
      this.fadeOutAt = -1;
      return;
    }
    if (!P.bgm.on) return;
    const spb = 60 / Math.max(20, P.bgm.bpm); // 4분음표 길이
    const dur = spb / 4; // 16분
    // 탭이 백그라운드였다가 돌아오면 과거가 잔뜩 밀려 있다 — 한꺼번에 쏟지 않고 현재로 당긴다
    if (this.nextTime < now - 0.25) this.nextTime = now + 0.02;
    let guard = 0;
    while (this.nextTime < now + P.bgm.lookahead && guard++ < 128) {
      this.scheduleStep(this.step, this.nextTime, dur);
      this.nextTime += dur;
      this.step = (this.step + 1) % TOTAL_STEPS;
    }
  }

  // ------------------------------------------------------------ 성부
  /** 오프라인 렌더 전용 진입점 (renderBgmOffline). 게임 경로는 안 쓴다. */
  scheduleForOffline(i: number, t: number, dur: number): void {
    this.scheduleStep(i, t, dur);
  }

  /** 오프라인 렌더 전용 — 페이드 없이 버스 음량을 바로 세운다. */
  forceBusGain(v: number): void {
    if (this.bus) this.bus.gain.value = v;
  }

  private scheduleStep(i: number, t: number, dur: number): void {
    const bar = Math.floor(i / STEPS_PER_BAR);
    const s = i % STEPS_PER_BAR;
    const root = ROOTS[bar];
    const triad = TRIADS[bar];
    const B = P.bgm;

    // 멜로디
    for (const [st, midi, len] of MELODY[bar]) {
      if (st !== s) continue;
      this.tone('square', hz(midi), t, dur * len * 0.92, B.lead, B.leadCut, 0.004);
      if (B.leadOct > 0) this.tone('square', hz(midi + 12), t, dur * len * 0.5, B.lead * B.leadOct, B.leadCut * 1.6, 0.004);
    }
    // 뒷박 화음 스탭 — 발랄함의 정체
    if (s % 4 === 2) {
      for (const m of triad) this.tone('square', hz(m), t, dur * 1.1, B.chop, B.chopCut, 0.003);
    }
    // 베이스
    const bi = BASS_STEPS.indexOf(s);
    if (bi >= 0) this.tone('triangle', hz(root + BASS_INT[bi]), t, dur * 1.7, B.bass, B.bassCut, 0.006);
    // 마디 첫박 반짝임
    if (s === 0 && B.bell > 0) {
      const f = hz(triad[2] + 12);
      this.tone('sine', f, t, dur * 6, B.bell, 20000, 0.002);
      this.tone('sine', f * 2, t, dur * 3, B.bell * 0.4, 20000, 0.002);
    }
    // 드럼
    if (KICK.includes(s)) this.kick(t, s === 0 ? 1 : 0.7);
    if (SNARE.includes(s)) this.snare(t);
    if (HAT.includes(s)) this.hat(t, s % 4 === 0 ? 1 : 0.55);
  }

  /** 짧은 감쇠 음 하나. 필터를 거쳐 딱딱한 square 를 둥글게 만든다. */
  private tone(type: OscillatorType, f: number, t: number, dur: number, gain: number, cut: number, atk: number): void {
    if (!this.ctx || !this.bus || gain <= 0.0001) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(f, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + atk);
    g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(Math.min(cut, 20000), t);
    lp.Q.value = 0.7;
    osc.connect(lp);
    lp.connect(g);
    g.connect(this.bus);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private burst(hpOrBp: 'highpass' | 'bandpass', freq: number, q: number, t: number, dur: number, gain: number): void {
    if (!this.ctx || !this.bus || !this.noise || gain <= 0.0001) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = hpOrBp;
    f.frequency.setValueAtTime(freq, t);
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    src.connect(f);
    f.connect(g);
    g.connect(this.bus);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  private kick(t: number, strength: number): void {
    if (!this.ctx || !this.bus) return;
    const B = P.bgm;
    if (B.drum <= 0) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(48, t + 0.09);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(B.drum * strength, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0008, t + 0.16);
    osc.connect(g);
    g.connect(this.bus);
    osc.start(t);
    osc.stop(t + 0.19);
  }

  private snare(t: number): void {
    const B = P.bgm;
    this.burst('bandpass', 1900, 0.8, t, 0.11, B.drum * 0.55);
    this.tone('triangle', 196, t, 0.07, B.drum * 0.25, 3000, 0.002);
  }

  private hat(t: number, strength: number): void {
    this.burst('highpass', 7800, 0.7, t, 0.035, P.bgm.drum * 0.16 * strength);
  }
}

export const bgm = new Bgm();

/**
 * 오프라인 렌더 — 같은 시퀀서로 seconds 초를 통째로 구워 AudioBuffer 로 돌려준다.
 * **미리 들어 보기 전용이다** (tools/bgm-preview.mjs). 게임은 이 경로를 안 쓴다.
 * 시퀀서 코드를 그대로 쓰므로 미리듣기와 실제 재생이 어긋날 수 없다.
 */
export async function renderBgmOffline(seconds: number, sampleRate = 44100): Promise<AudioBuffer> {
  const octx = new OfflineAudioContext(1, Math.ceil(seconds * sampleRate), sampleRate);
  const out = octx.createGain();
  out.gain.value = 1;
  out.connect(octx.destination);
  const b = new Bgm();
  b.attach(octx as unknown as AudioContext, out);
  b.forceBusGain(P.bgm.gain);
  const dur = 60 / Math.max(20, P.bgm.bpm) / 4;
  let t = 0;
  let step = 0;
  while (t < seconds) {
    b.scheduleForOffline(step, t, dur);
    t += dur;
    step = (step + 1) % TOTAL_STEPS;
  }
  return octx.startRendering();
}

